import { Injectable, inject } from '@angular/core';
import { Plugin } from '@jeppesen-foreflight/jmkw-types';
import { TwcGroupLayer } from '../../../layer-type-services/group/twc-group.layer';
import { TEMPS_WINDS_ALOFT_LEGEND } from './temps-winds-aloft.legend';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { TwcVectorTileLayer } from '../../../layer-type-services/vector-tile/twc-vector-tile.layer';
import { TempsAloftPluginService } from './plugins/temps-aloft/temps-aloft.plugin.service';
import { WindsAloftPluginService } from './plugins/winds-aloft/winds-aloft.plugin.service';
import { combineLatest, map } from 'rxjs';

/**
 * READ THE ANNOTATIONS
 * In v2.x winds and temps were a group layer. Others, we managed to convince the team
 * to let us split up into multiple plugins. Here, they wanted them to remain as a group.
 * This should now be refactorable into a group of layers without needing to keep the plugins
 * separate. This would resolve a lot of the complication that you see here. This work should
 * be performed when we move to HWS. As there is a lot of unknowns that could break the code
 * as part of that transition, it is best to leave this as is for now to avoid double work.
 */
@Injectable({
  providedIn: 'root'
})
export class TempsWindsAloftPluginService {
  private readonly _twcWindsAloftService = inject(WindsAloftPluginService);
  private readonly _twcTempsAloftService = inject(TempsAloftPluginService);
  private readonly _tempsAloftPlugin = this._twcTempsAloftService.plugin$;
  private readonly _windsAloftPlugin = this._twcWindsAloftService.plugin$;

  plugin$ = combineLatest([
    this._windsAloftPlugin,
    this._tempsAloftPlugin
  ]).pipe(
    map(([windsAloftPlugin, tempsAloftPlugin]) => {
      const windsLayer = windsAloftPlugin.layer as TwcVectorTileLayer;
      const tempsLayer = tempsAloftPlugin.layer as TwcVectorTileLayer;

      const groupLayer = new TwcGroupLayer({
        id: 'winds-temps-aloft',
        label: 'Winds Temps Aloft',
        zOrder: 95,
        layers: [windsLayer, tempsLayer],
        selectedTime: windsLayer?.selectedTime,
        selectedFlightLevel: windsLayer?.selectedFlightLevel
      });

      this.setupMetadataWatch(groupLayer, windsLayer, tempsLayer);

      const legend = {
        ...TEMPS_WINDS_ALOFT_LEGEND,
        data: {
          ...TEMPS_WINDS_ALOFT_LEGEND.data,
          pluginIDs: ['winds-aloft', 'temps-aloft']
        }
      };

      return new Plugin({
        id: 'winds-temps-aloft',
        layer: groupLayer,
        legend: legend
      });
    })
  );

  /**
   * Set up metadata watching to propagate updates to the group layer
   * Uses reactiveUtils for better reactivity handling
   */
  private setupMetadataWatch(
    groupLayer: TwcGroupLayer,
    windsLayer: TwcVectorTileLayer,
    tempsLayer: TwcVectorTileLayer
  ): void {
    const updateGroupMetadata = () => {
      const windsUpdatedAt = windsLayer.metadata?.['updatedAt'];
      const tempsUpdatedAt = tempsLayer.metadata?.['updatedAt'];

      let updatedAt: number | undefined;
      if (windsUpdatedAt && tempsUpdatedAt) {
        updatedAt = Math.max(windsUpdatedAt, tempsUpdatedAt);
      } else {
        updatedAt = windsUpdatedAt || tempsUpdatedAt;
      }

      if (updatedAt) {
        groupLayer.metadata = {
          ...groupLayer.metadata,
          updatedAt
        };
      }
    };

    reactiveUtils.watch(
      () => windsLayer.metadata,
      () => updateGroupMetadata(),
      { initial: true }
    );

    reactiveUtils.watch(
      () => tempsLayer.metadata,
      () => updateGroupMetadata(),
      { initial: true }
    );
  }
}
